/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Checkbox, FormControlLabel, FormGroup, IconButton, ListItemButton, ListItemText, Popover, Tooltip} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArgosButton, {ArgosLoadingButton} from "../../../../components/Buttons/ArgosButton";
import {
  ConfigSensorWizardState,
  defaultDefinePropertiesStepData,
  defaultSelectSensorStepData,
  selectAdasProgramsArchive,
  selectEditSensorBomState,
  selectMoveSensorLocationsState,
  selectSensorBomState,
  setConfigSensorWizardState,
  setIsEditLocationsEnabled,
  setIsMoveLocationsEnabled,
  setSensorBomData,
  setSensorBomIsReadOnly,
  setSensorBomWarningVehIds,
  setSensorLocations
} from "../../../../../redux/ducks/AdasPrograms/slice";
import {EAdasProgramsEditSensorDialogType} from "./types";
import Add from "@mui/icons-material/Add";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import React, {useEffect, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import styled from "styled-components";
import * as COLORS from "../../../../../constants/colors";
import {tableButtonGray} from "../../../../../constants/colors";
import {SensorBillOfMaterialsLocationMap} from "./SensorBillOfMaterialsLocationMap";
import {SensorBillOfMaterialsList} from "./SensorBillOfMaterialsList";
import {theme} from "../../../../../constants/theme";
import {useGetIsUserAdmin, useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {ADAS_PROGRAMS_CODE_CONFIG} from "../../config";
import {EAdasProgramSensorBomCheckboxTypes, TSensorLocations} from "../types";
import {usePostRequest} from "../../../../../api/helper";
import ADAS_PROGRAMS_CONSTANTS from "../../constants";
import useGetUrlParams from "../../../../../hooks/useGetUrlParams";
import {usePollingEffect} from "../../../../../hooks/usePollingEffect";
import {ArgosWarningIcon} from "../../../../components/Icons/ArgosActionIcons";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../../vehiclePrograms/constants";
import {toast} from "react-hot-toast";
import {cloneDeep} from "lodash";
import {Nullable} from "../../../../../utils/globalTypeUtils";
import DraggableResizableDialog from "./DraggableResizableDialog/DraggableResizableDialog";
import {MRT_ColumnDef, MRT_GlobalFilterTextField, MRT_ToggleFiltersButton, MRT_ToggleGlobalFilterButton} from "material-react-table";
import Box from "@mui/material/Box";
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";

const Card = styled.div({
  border: `1px solid ${COLORS.baseBlue}`,
  borderRadius: '10px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'auto'
})
const CardHeader = styled(ListItemButton)({
  color: theme.layout.sidebar.backgroundColor,
})


const initialCollapsedCardsState = [false, false, false]
const topBottomPadding = 20

export const useGetHandleFetchSensorBomIsReadOnly = () => {
  const dispatch = useAppDispatch()
  const handleCheckSensorBomReadOnly = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.isSensorBomReadOnly)
  //const handleGetVehIds = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.getVehIdsSensorBom)
  return (adasProgramId: string, gmid: string, isAdmin: boolean) => {
    if (adasProgramId && adasProgramId !== '') {
      handleCheckSensorBomReadOnly({
        "adasProgramId": adasProgramId,
        "currentUserGmid": gmid,
        "isAdmin": isAdmin,
        "isBoM" : true
      }).then(r => {
        dispatch(setSensorBomIsReadOnly({adasProgramId, isReadOnly: r.isReadOnly}))
        if (r.isReadOnly)
            dispatch(setSensorBomWarningVehIds({adasProgramId, data: r.data}))
      }).catch(e => {
        console.error(e)
      })
    }
  }
}
const SensorBom = () => {

  const hasWriteAccess = useHasWriteAccess()
  const dispatch = useAppDispatch()

  const editLocationsEnabled = useAppSelector(state => state.adasPrograms.isEditLocationsEnabled)
  const editLocationsButtonColor = useMemo(() => editLocationsEnabled ? 'lightgreen' : 'initial', [editLocationsEnabled])
  const moveLocationsEnabled = useAppSelector(state => selectMoveSensorLocationsState(state).isMoveLocationsEnabled)
  const moveLocationsButtonColor = useMemo(() => moveLocationsEnabled ? 'lightgreen' : 'initial', [moveLocationsEnabled])
  const moveLocationKey = useAppSelector(state => selectMoveSensorLocationsState(state).locationKey)

  const [collapsedCards, setCollapsedCards] = useState<boolean[]>(initialCollapsedCardsState)
  const onHeaderClick = (e: any, index: number) => {
    setCollapsedCards(prevState => {
      return modifyArray(index, prevState, (oldValue: boolean) => !oldValue)
    })
    e.stopPropagation()
  }


  const CARD_DATA = useMemo(() => [
    {
      header: 'Sensor Bill of Materials Location Map',
      content: <SensorBillOfMaterialsLocationMap/>,
    },
    {
      header: 'Sensor Bill of Materials List (Camera)',
      content: <SensorBillOfMaterialsList sensorBomListType={EAdasProgramSensorBomCheckboxTypes.CAMERAS}/>,
    },
    {
      header: 'Sensor Bill of Materials List (Radar)',
      content: <SensorBillOfMaterialsList sensorBomListType={EAdasProgramSensorBomCheckboxTypes.RADARS}/>,
    },
    {
      header: 'Sensor Bill of Materials List (Lidar)',
      content: <SensorBillOfMaterialsList sensorBomListType={EAdasProgramSensorBomCheckboxTypes.LIDARS} />,
    },
    {
      header: 'Sensor Bill of Materials List (Ultrasonic)',
      content: <SensorBillOfMaterialsList sensorBomListType={EAdasProgramSensorBomCheckboxTypes.ULTRASONIC} />,
    }
  ], [])

  const {adasProgramId} = useGetUrlParams()
  const isAdmin: boolean = useGetIsUserAdmin()
  const gmid = useAppSelector(state => selectCurrentUser(state).id)

  /**
   * Check if sensor bom should be read only
   */
  const handleCheckSensorBomReadOnly = useGetHandleFetchSensorBomIsReadOnly()

  usePollingEffect(
    async () => handleCheckSensorBomReadOnly(adasProgramId, gmid, isAdmin),
    [],
    {interval: 4000}
  )
  const htmlURLString = () => {
    return ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.sensorBomReadOnlyDevStagesMessage + reduxGetSensorBomWarningVehIds.map((item: any) => "<a target='blank' style='color:orange' href='" + VEHICLE_PROGRAMS_CONSTANTS.itemDetails.constructRouteUrl(item.id) + "'><strong>" + item.name + "</strong></a>").join(', ')
  }
  const reduxIsSensorBomReadOnly = useAppSelector(state => selectSensorBomState(state).isReadOnly?.[adasProgramId]) ?? false
  const reduxGetSensorBomWarningVehIds = useAppSelector(state => selectSensorBomState(state).vehProgs?.[adasProgramId]) ?? []
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const sensorLocations = useAppSelector(state => selectEditSensorBomState(state).sensorLocations)
  const addSensorLocationHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=addSensorLocation')

  const [savingSensorLocations, setSavingSensorLocations] = useState(false)
  const onSaveSensorLocations = () => {
    if (adasProgramId && sensorLocations) {
      const deepCopySensorLocations = cloneDeep(sensorLocations)
      // set each sensorLocationState to 'saved'
      Object.keys(deepCopySensorLocations).forEach((key: string) => {
        deepCopySensorLocations[key].sensorLocationState = 'saved'
      })
      setSavingSensorLocations(true)
      addSensorLocationHandle({sensorLocations: deepCopySensorLocations, adasProgramId, requestType: 'changeVisibilitySensorLocations'})
        .then((res) => {
          dispatch(setSensorBomData(res))
        setIsSelectSensorLocationsDialogOpen(false)
          toast.success('Sensor locations saved')
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error saving sensor locations')
        }).finally(() => {
        setSavingSensorLocations(false)
      })
    }
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, sensorId: string) => {
    if (!sensorLocations) return

    const newSensorLocations = {...sensorLocations}

    newSensorLocations[sensorId] = {
      ...newSensorLocations[sensorId],
      hidden: e.target.checked
    }
    dispatch(setSensorLocations(newSensorLocations))
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handlePopoverClick = (event: React.MouseEvent<HTMLElement>) => {
    // setAnchorEl(event.currentTarget);
    openSelectSensorLocationsDialog()
    setTempSensorLocations(sensorLocations)
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    // compare the hidden properties of the tempSensorLocations and sensorLocations, if same do nothing, else update sensorLocations
    if (tempSensorLocations && sensorLocations && hasSensorLocationsChanged) {
        onSaveSensorLocations()
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'sensor-popover' : undefined;

  const sortedSensorLocationKeys = useMemo(() => {
    return sensorLocations ? Object.keys(sensorLocations).sort() : [];
  }, [sensorLocations]);

  const [tempSensorLocations, setTempSensorLocations] = useState<Nullable<TSensorLocations>>(null)

  const sensorLocationsTableData = useMemo(() => {
    return sensorLocations ? sortedSensorLocationKeys.map(sensorLocation => {
      return {
        sensorLocation
      }
    }) : []
  }, [sensorLocations, sortedSensorLocationKeys])

  const getLocationFullName = (locationKey: string) => {
    const location = ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.locationOptions.find((option: any) => option.locationKey === locationKey);
    return location ? location.sensorLocation : locationKey;
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
    {
      accessorKey: "sensorLocation",
      header: "Sensor Location",
      size: 250,
      Cell: ({ cell }) => `${getLocationFullName(cell.getValue<string>())} (${cell.getValue<string>()})`,
    },
  ], [sensorLocations])

  const [rowSelection, setRowSelection] = useState<any>({});
  // Initialize row selection state based on hidden state
  useEffect(() => {
    if (sensorLocations && sortedSensorLocationKeys) {
      const initialRowSelection = sortedSensorLocationKeys.reduce((acc: any, key) => {
        if (!sensorLocations[key].hidden) {
          acc[key] = true;
        }
        return acc;
      }, {});
      setRowSelection(initialRowSelection);
    }
  }, [sortedSensorLocationKeys]);


  const handleRowSelectionChange = (newRowSelection: any) => {
    const newSensorLocations = cloneDeep(sensorLocations);
    if (!newSensorLocations) return;
    sortedSensorLocationKeys.forEach((key) => {
      newSensorLocations[key].hidden = !newRowSelection[key];
    });
    dispatch(setSensorLocations(newSensorLocations));
  };

  /**
   * Select sensors dialog
   */
  const [isSelectSensorLocationsDialogOpen, setIsSelectSensorLocationsDialogOpen] = useState(false);
  const openSelectSensorLocationsDialog = () => setIsSelectSensorLocationsDialogOpen(true);
  const cancelSelectSensorLocationsDialog = () => {
    setIsSelectSensorLocationsDialogOpen(false);
    dispatch(setSensorLocations(tempSensorLocations))
  }
  const onSaveSelectSensorLocationsDialog = () => {
    if (tempSensorLocations && sensorLocations && hasSensorLocationsChanged) {
        onSaveSensorLocations()
    }
  }

  const hasSensorLocationsChanged = useMemo(() => {
    if (tempSensorLocations && sensorLocations) {
      for (const key in tempSensorLocations) {
        if (!(tempSensorLocations[key].hidden === undefined && sensorLocations[key].hidden === false)
          && tempSensorLocations[key].hidden !== sensorLocations[key].hidden) {
          return true
        }
      }
    }
    return false
  }, [sensorLocations, tempSensorLocations])

  const footerItems = useMemo(() => {
    return (
      <>
        <ArgosButton onClick={cancelSelectSensorLocationsDialog}>
          Cancel
        </ArgosButton>
        <ArgosLoadingButton
          isLoading={savingSensorLocations}
          onClick={onSaveSelectSensorLocationsDialog}
          disabled={!hasSensorLocationsChanged}
        >
          Save
        </ArgosLoadingButton>
      </>
    )
  }, [cancelSelectSensorLocationsDialog, onSaveSelectSensorLocationsDialog, savingSensorLocations, hasSensorLocationsChanged])

  return (
    <ArgosFlexbox vertical style={{
      flexGrow: 1,
      width: '100%',
      padding: `${topBottomPadding}px ${10}px ${topBottomPadding}px ${10}px`,
      overflow: 'auto',
      height: '100%'
    }}>
      {
        hasWriteAccess ? (
          <ArgosFlexbox style={{justifyContent: 'right'}}>
            {reduxIsSensorBomReadOnly && !reduxIsProgramArchive &&
              <span>
                <Tooltip
                  title={
                    <div dangerouslySetInnerHTML={{__html: htmlURLString()}}/>}
                  style={{cursor: 'default'}}
                  placement={'left'}
                >
                  <IconButton
                    sx={{
                      cursor: 'default'
                    }}
                    disableRipple
                  >
                    <ArgosWarningIcon style={{cursor: 'default'}}/>
                  </IconButton>
                </Tooltip>
              </span>
            }
            {
              ADAS_PROGRAMS_CODE_CONFIG.ADMIN_MODIFIERS &&
              <ArgosFlexbox>
                <ArgosButton
                  style={{backgroundColor: editLocationsButtonColor, width: 'fit-content', height: 'fit-content'}}
                  onClick={() => dispatch(setIsEditLocationsEnabled())}
                >
                  Add Locations with Click
                </ArgosButton>
                <ArgosButton
                  style={{backgroundColor: moveLocationsButtonColor, width: 'fit-content', height: 'fit-content'}}
                  onClick={() => dispatch(setIsMoveLocationsEnabled())}
                >
                  Move location: {moveLocationKey}
                </ArgosButton>
                <ArgosButton
                  style={{backgroundColor: moveLocationsButtonColor, width: 'fit-content', height: 'fit-content'}}
                  onClick={() => onSaveSensorLocations()}
                >
                  Save locations
                </ArgosButton>
              </ArgosFlexbox>
            }
            <ArgosFlexbox>
              <div style={{display: 'flex'}}>
                <ArgosLoadingButton disabled={reduxIsSensorBomReadOnly || reduxIsProgramArchive} onClick={handlePopoverClick}>Select sensor locations</ArgosLoadingButton>
              </div>
              <DraggableResizableDialog
                isOpen={isSelectSensorLocationsDialogOpen}
                // onClose={closeDialog}
                footerItems={footerItems}
              >
                <ArgosTableSimple
                  columns={columns}
                  data={sensorLocationsTableData}

                  // select
                  enableRowSelection
                  onRowSelectionChange={(updater: any) => {
                    setRowSelection((prev: any) => {
                      const newState = updater(prev);
                      handleRowSelectionChange(newState);
                      return newState;
                    });
                  }}
                  state={{
                    rowSelection,
                    columnOrder: [...(columns.map(col => col.id ?? '') ?? []), 'mrt-row-select']
                  }}
                  getRowId={(originalRow) => originalRow.sensorLocation}

                  initialState={{density: 'compact'}}
                  editDisplayMode="cell"
                  enableEditing
                  enableBottomToolbar={false}
                  enablePagination={false}

                  renderToolbarInternalActions={({table}) => (
                    <Box>
                      <MRT_GlobalFilterTextField table={table}/>
                      <MRT_ToggleGlobalFilterButton table={table} style={{color: tableButtonGray}}/>
                      <MRT_ToggleFiltersButton table={table} style={{color: tableButtonGray}}/>
                    </Box>
                  )}


                />
              </DraggableResizableDialog>
            </ArgosFlexbox>
            <Tooltip title={'Add sensor'}>
              <IconButton
                disabled={reduxIsSensorBomReadOnly || reduxIsProgramArchive}
                onClick={() => {
                  const wizardState: ConfigSensorWizardState = {
                    action: EAdasProgramsEditSensorDialogType.ADD_GLOBAL,
                    selectSensorStep: {...defaultSelectSensorStepData},
                    definePropertiesStep: defaultDefinePropertiesStepData,
                  }
                  dispatch(setConfigSensorWizardState(wizardState))
                }}
                style={{width: 'fit-content', height: 'fit-content'}}
              >
                <Add/>
              </IconButton>
            </Tooltip>
          </ArgosFlexbox>
        ) : null
      }
      <div style={{
        flexGrow: 1,
        gap: 10, overflow: 'auto'
      }}>
        {CARD_DATA.map((card, index) => {
          // is content rerendering every time?
          const content = card.content
          return (
            <Card key={index} style={{height: 'fit-content', paddingBottom: '40px', marginBottom: '20px'}}>
              <CardHeader style={{flexGrow: 1, width: '100%', alignItems: 'center'}}
                          onClick={(e) => onHeaderClick(e, index)}
              >
                <ListItemText primary={card.header}></ListItemText>
                <IconButton style={{marginLeft: 'auto'}} onClick={(e) => onHeaderClick(e, index)}>
                  {!collapsedCards[index] ? <ExpandLess/> : <ExpandMore/>}
                </IconButton>
              </CardHeader>
              <div style={{
                flexGrow: 1,
                display: collapsedCards[index] ? 'none' : 'initial',
                width: '100%',
                height: '100%',
                overflow: 'auto',
                padding: 10,
              }}>
                {content}
              </div>
            </Card>
          )
        })}
      </div>
    </ArgosFlexbox>
  )
}

const modifyArray = (index: number, array: any[], setVal: (oldValue: any) => any) => {
  return [...array.slice(0, index),
    setVal(array[index]),
    ...array.slice(index + 1)]
}

export default SensorBom